import { useState, useRef, useEffect, useId, useCallback } from "react";
import Keyboard from "simple-keyboard";
import "simple-keyboard/build/css/index.css";
import "./TextInputWithKeyboard.css";

const BLUR_DELAY_MS = 200;

const DEFAULT_LAYOUT = {
  default: [
    "1 2 3 4 5 6 7 8 9 0 {bksp}",
    "q w e r t y u i o p",
    "a s d f g h j k l",
    "{shift} z x c v b n m {shift}",
    ". - _ {space} {enter}",
  ],
  shift: [
    "! @ # $ % ^ & * ( ) {bksp}",
    "Q W E R T Y U I O P",
    "A S D F G H J K L",
    "{shift} Z X C V B N M {shift}",
    ". - _ {space} {enter}",
  ],
};

const EMAIL_LAYOUT = {
  default: [
    "1 2 3 4 5 6 7 8 9 0 {bksp}",
    "q w e r t y u i o p",
    "a s d f g h j k l",
    "{shift} z x c v b n m {shift}",
    "@ {.com} . - _ {space} {enter}",
  ],
  shift: [
    "1 2 3 4 5 6 7 8 9 0 {bksp}",
    "Q W E R T Y U I O P",
    "A S D F G H J K L",
    "{shift} Z X C V B N M {shift}",
    "@ {.com} . - _ {space} {enter}",
  ],
};

export default function TextInputWithKeyboard({
  type = "text",
  value = "",
  onChange,
  onBlur,
  onKeyDown,
  showKeyboard: enableKeyboard = false,
  placeholder = "",
  disabled = false,
  className = "",
  inputClassName = "",
  inputRef: forwardedRef,
  name,
  id,
}) {
  const [keyboardVisible, setKeyboardVisible] = useState(false);
  const blurTimeoutRef = useRef(null);
  const inputRef = useRef(null);
  const keyboardDivRef = useRef(null);
  const keyboardInstanceRef = useRef(null);
  const layoutNameRef = useRef("default");

  // simple-keyboard requires the target element to have a class containing "simple-keyboard".
  // Use a stable unique class per instance so multiple keyboards can coexist.
  const uid = useId().replace(/:/g, "");
  const keyboardClass = `simple-keyboard-${uid}`;

  // Stable refs to avoid stale closures inside the keyboard callbacks
  const onChangeRef = useRef(onChange);
  useEffect(() => {
    onChangeRef.current = onChange;
  });

  // Password fields show plain text while the keyboard is open (no physical keyboard to peek over)
  const effectiveType = type === "password" && keyboardVisible ? "text" : type;

  const handleKeyPress = (button) => {
    if (button === "{shift}" || button === "{lock}") {
      const next =
        layoutNameRef.current === "default" ? "shift" : "default";
      layoutNameRef.current = next;
      keyboardInstanceRef.current?.setOptions({ layoutName: next });
    } else if (button === "{enter}") {
      inputRef.current?.blur();
    } else if (button === "{.com}") {
      const currentInput = keyboardInstanceRef.current?.getInput() ?? "";
      const newValue = currentInput + ".com";
      keyboardInstanceRef.current?.setInput(newValue);
      onChangeRef.current?.(newValue);
    }
  };

  const handleKeyPressRef = useRef(handleKeyPress);
  useEffect(() => {
    handleKeyPressRef.current = handleKeyPress;
  });

  useEffect(() => {
    if (!keyboardVisible || !keyboardDivRef.current) return;

    layoutNameRef.current = "default";

    keyboardInstanceRef.current = new Keyboard(keyboardDivRef.current, {
      onChange: (input) => onChangeRef.current?.(input),
      onKeyPress: (button) => handleKeyPressRef.current(button),
      layout: type === "email" ? EMAIL_LAYOUT : DEFAULT_LAYOUT,
      layoutName: "default",
      display: {
        "{bksp}": "⌫",
        "{enter}": "↵",
        "{shift}": "⇧",
        "{lock}": "⇪",
        "{space}": "  ",
        "{.com}": ".com",
      },
      theme: "hg-theme-default tw-keyboard",
      preventMouseDownDefault: true,
      physicalKeyboardHighlight: false,
    });

    keyboardInstanceRef.current.setInput(value || "");

    return () => {
      keyboardInstanceRef.current?.destroy();
      keyboardInstanceRef.current = null;
    };
  }, [keyboardVisible, type]); // eslint-disable-line react-hooks/exhaustive-deps

  // Keep internal keyboard state in sync when value is changed externally (e.g. form reset)
  useEffect(() => {
    if (keyboardInstanceRef.current) {
      keyboardInstanceRef.current.setInput(value || "");
    }
  }, [value]);

  const handleFocus = () => {
    // Defer the readOnly removal so Chrome evaluates autofill eligibility
    // while the field is still readOnly (and skips it), then we unlock
    // the field for physical keyboard input after Chrome's check is done.
    if (enableKeyboard && inputRef.current) {
      const el = inputRef.current;
      setTimeout(() => { el.readOnly = false; }, 50);
    }
    if (!enableKeyboard) return;
    if (blurTimeoutRef.current) {
      clearTimeout(blurTimeoutRef.current);
      blurTimeoutRef.current = null;
    }
    setKeyboardVisible(true);
  };

  const handleBlur = (e) => {
    // Re-lock so the next focus also skips Chrome's autofill check.
    if (enableKeyboard && inputRef.current) {
      inputRef.current.readOnly = true;
    }
    blurTimeoutRef.current = setTimeout(() => {
      setKeyboardVisible(false);
      blurTimeoutRef.current = null;
    }, BLUR_DELAY_MS);
    onBlur?.(e);
  };

  const handleInputChange = (e) => {
    onChange?.(e.target.value);
    keyboardInstanceRef.current?.setInput(e.target.value);
  };

  // Stable ref callback — only fires on mount/unmount, not on every re-render.
  // This prevents re-renders (e.g. each physical keystroke) from re-running
  // `el.readOnly = true` and locking out the physical keyboard mid-typing.
  const setInputRef = useCallback(
    (el) => {
      if (el && enableKeyboard) el.readOnly = true;
      inputRef.current = el;
      if (forwardedRef) {
        if (typeof forwardedRef === "function") forwardedRef(el);
        else forwardedRef.current = el;
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    []
  );

  return (
    <div className={`text-input-with-keyboard ${className}`}>
      <input
        ref={setInputRef}
        type={effectiveType}
        className={`form-control form-control-lg tw-keyboard-input ${inputClassName}`}
        placeholder={placeholder}
        value={value}
        onChange={handleInputChange}
        onFocus={handleFocus}
        onBlur={handleBlur}
        onKeyDown={onKeyDown}
        disabled={disabled}
        name={name}
        id={id}
        autoComplete="new-password"
      />
      {enableKeyboard && keyboardVisible && !disabled && (
        <div className="tw-keyboard-panel">
          <div ref={keyboardDivRef} className={keyboardClass} />
        </div>
      )}
    </div>
  );
}
